import { Fragment, useEffect, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import type { AdminFileGroup, AdminDetailRow } from "./AdminApp";
import type { AdminSubmissionDetail, AdminSubmissionFile } from "./admin-api";
import { getAdminReviewChecklistStorageKey, getAdminReviewRowItemIds, getAdminReviewRowLines, groupAdminReviewRows, parseAdminReviewChecklist, serializeAdminReviewChecklist, toggleAdminReviewChecklistItems } from "./admin-review-checklist";
import "./admin-review.css";

export function AdminSubmissionReviewSheet({ submission, sections, fileGroups, getFilePreviewPath, onClose }: {
  submission: AdminSubmissionDetail;
  sections: Array<{ title: string; rows: AdminDetailRow[] }>;
  fileGroups: AdminFileGroup[];
  getFilePreviewPath: (file: AdminSubmissionFile) => string;
  onClose: () => void;
}) {
  const [showEmpty, setShowEmpty] = useState(false);
  const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(() => loadCheckedItemIds(submission.id));
  useEffect(() => {
    setCheckedItemIds(loadCheckedItemIds(submission.id));
  }, [submission.id]);

  function toggleCheckedItems(itemIds: readonly string[]) {
    setCheckedItemIds((current) => {
      const next = toggleAdminReviewChecklistItems(current, itemIds);
      saveCheckedItemIds(submission.id, next);
      return next;
    });
  }

  function resetCheckedItems() {
    setCheckedItemIds(new Set());
    clearCheckedItemIds(submission.id);
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const visibleSections = sections
    .map((section) => ({ title: section.title, rows: section.rows.filter((row) => row.kind !== "adminPassword" && (showEmpty || Boolean(row.value))) }))
    .filter((section) => section.rows.length > 0);
  const visibleCheckedCount = visibleSections.flatMap((section) => section.rows.flatMap(getAdminReviewRowItemIds)).filter((itemId) => checkedItemIds.has(itemId)).length;

  return (
    <div className="admin-review-overlay" role="presentation">
      <section className="admin-review-sheet" role="dialog" aria-modal="true" aria-labelledby="admin-review-title">
        <header className="admin-review-header">
          <div>
            <p className="admin-review-eyebrow">제출 내용 검수</p>
            <h2 id="admin-review-title">{submission.storeNameKo || "상점명 없음"}</h2>
            <dl className="admin-review-meta">
              <div><dt>카테고리</dt><dd>{submission.category.label || submission.category.categoryId}</dd></div>
              <div><dt>제출일시</dt><dd>{formatReviewDate(submission.submittedAt)}</dd></div>
              <div><dt>제출 ID</dt><dd>{submission.id}</dd></div>
            </dl>
          </div>
          <div className="admin-review-actions">
            <label className="admin-review-empty-toggle">
              <input type="checkbox" checked={showEmpty} onChange={(event) => setShowEmpty(event.target.checked)} />
              <span>빈 항목 표시</span>
            </label>
            <span className="admin-review-check-status" aria-live="polite">{visibleCheckedCount}개 확인함</span>
            <button className="button secondary compact" type="button" onClick={resetCheckedItems} disabled={checkedItemIds.size === 0}>검수 표시 초기화</button>
            <button className="button secondary compact" type="button" onClick={() => window.print()}>인쇄</button>
            <button className="modal-close" type="button" onClick={onClose} aria-label="전체 화면 검수 닫기" autoFocus>×</button>
          </div>
        </header>

        <div className="admin-review-content">
          {visibleSections.map(({ title, rows }) => {
            return (
              <ReviewSection key={title} title={title} onToggle={() => toggleCheckedItems(rows.flatMap(getAdminReviewRowItemIds))}>
                {groupAdminReviewRows(rows).map((group) => {
                  const list = (
                    <dl className="admin-review-definition-list">
                      {group.rows.map(({ row, label }) => {
                        const lines = getAdminReviewRowLines(row);
                        if (lines) return <LineCheckDefinitionRow key={row.id} label={label} lines={lines} checkedItemIds={checkedItemIds} onToggle={toggleCheckedItems} />;
                        return (
                          <CheckableDefinitionRow key={row.id} checked={checkedItemIds.has(row.id)} onToggle={() => toggleCheckedItems([row.id])}>
                            <dt>{label}</dt><dd>{row.value || "-"}</dd>
                          </CheckableDefinitionRow>
                        );
                      })}
                    </dl>
                  );
                  if (!group.title) return <Fragment key={group.id}>{list}</Fragment>;
                  const groupItemIds = group.rows.flatMap(({ row }) => getAdminReviewRowItemIds(row));
                  const groupChecked = groupItemIds.every((itemId) => checkedItemIds.has(itemId));
                  return (
                    <section key={group.id} className={`admin-review-group ${groupChecked ? "is-checked" : ""}`}>
                      <h4><button className="admin-review-group-toggle" type="button" aria-pressed={groupChecked} aria-label={`${group.title} 항목 일괄 확인 표시`} onClick={() => toggleCheckedItems(groupItemIds)}>{group.title}</button></h4>
                      {list}
                    </section>
                  );
                })}
              </ReviewSection>
            );
          })}

          <ReviewSection title="첨부 파일">
            {fileGroups.length === 0 ? <p className="admin-review-empty">첨부한 사진·영상·서류가 없습니다.</p> : (
              <div className="admin-review-file-groups">
                {fileGroups.map((group) => (
                  <section key={group.key} className="admin-review-file-group">
                    <h4>{group.label} <span>{group.files.length}개</span></h4>
                    <div className="admin-review-file-grid">
                      {group.files.map((file) => <ReviewFileCard file={file} getFilePreviewPath={getFilePreviewPath} key={file.id} />)}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </ReviewSection>
        </div>
      </section>
    </div>
  );
}

function CheckableDefinitionRow({ checked, children, onToggle }: { checked: boolean; children: ReactNode; onToggle: () => void }) {
  return (
    <div className={`admin-review-checkable ${checked ? "is-checked" : ""}`} role="button" tabIndex={0} aria-pressed={checked} onClick={onToggle} onKeyDown={(event) => activateWithKeyboard(event, onToggle)}>
      {children}
    </div>
  );
}

function LineCheckDefinitionRow({ label, lines, checkedItemIds, onToggle }: {
  label: string;
  lines: Array<{ id: string; text: string }>;
  checkedItemIds: ReadonlySet<string>;
  onToggle: (itemIds: readonly string[]) => void;
}) {
  const lineIds = lines.map((line) => line.id);
  const allChecked = lineIds.every((lineId) => checkedItemIds.has(lineId));
  return (
    <div className="admin-review-line-row">
      <dt className={`admin-review-checkable ${allChecked ? "is-checked" : ""}`} role="button" tabIndex={0} aria-pressed={allChecked} aria-label={`${label} 전체 확인 표시`} onClick={() => onToggle(lineIds)} onKeyDown={(event) => activateWithKeyboard(event, () => onToggle(lineIds))}>{label}</dt>
      <dd>
        <ul className="admin-review-line-list">
          {lines.map((line) => {
            const checked = checkedItemIds.has(line.id);
            return (
              <li key={line.id} className={`admin-review-checkable ${checked ? "is-checked" : ""}`} role="button" tabIndex={0} aria-pressed={checked} onClick={() => onToggle([line.id])} onKeyDown={(event) => activateWithKeyboard(event, () => onToggle([line.id]))}>{line.text}</li>
            );
          })}
        </ul>
      </dd>
    </div>
  );
}

function activateWithKeyboard(event: ReactKeyboardEvent<HTMLElement>, onToggle: () => void) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onToggle();
}

function loadCheckedItemIds(submissionId: string): Set<string> {
  try {
    return parseAdminReviewChecklist(localStorage.getItem(getAdminReviewChecklistStorageKey(submissionId)));
  } catch {
    return new Set();
  }
}

function saveCheckedItemIds(submissionId: string, checkedItemIds: ReadonlySet<string>) {
  try {
    localStorage.setItem(getAdminReviewChecklistStorageKey(submissionId), serializeAdminReviewChecklist(checkedItemIds));
  } catch {
    // Browser privacy settings or storage quotas must not interrupt review.
  }
}

function clearCheckedItemIds(submissionId: string) {
  try {
    localStorage.removeItem(getAdminReviewChecklistStorageKey(submissionId));
  } catch {
    // Browser privacy settings must not interrupt review.
  }
}

function ReviewSection({ title, children, onToggle }: { title: string; children: ReactNode; onToggle?: () => void }) {
  return (
    <section className="admin-review-section">
      <h3>{onToggle ? <button className="admin-review-section-toggle" type="button" aria-label={`${title} 항목 일괄 확인 표시`} onClick={onToggle}>{title}</button> : title}</h3>
      {children}
    </section>
  );
}

function ReviewFileCard({ file, getFilePreviewPath }: { file: AdminSubmissionFile; getFilePreviewPath: (file: AdminSubmissionFile) => string }) {
  const isImage = file.contentType.startsWith("image/");
  const previewPath = getFilePreviewPath(file);
  return (
    <a className="admin-review-file-card" href={isImage ? previewPath : file.adminDownloadPath} target="_blank" rel="noreferrer">
      {isImage ? <img src={previewPath} alt={`${file.fileName} 미리보기`} loading="lazy" /> : <span className="admin-review-file-name">{file.fileName}</span>}
      <small>{file.fileName}</small>
    </a>
  );
}

function formatReviewDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" }).format(date);
}
